import { Link, useNavigate, useLocation } from 'react-router-dom';
import dinoLogoFull from '@/assets/dino-logo-full.png';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { useChats } from '@/hooks/useChats';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DinoAvatar } from '@/components/DinoAvatar';
import { DinoLogo } from '@/components/DinoLogo';
import { ArchetypeMascot } from '@/components/ArchetypeMascot';
import { BookCard } from '@/components/BookCard';
import { CoverImage } from '@/components/CoverImage';
import { ReviewTBRButtons } from '@/components/ReviewTBRButtons';
import { useToast } from '@/hooks/use-toast';
import { normalizeOpenLibraryId, searchOpenLibrary } from '@/lib/searchUtils';
import { BookResult, UserResult } from '@/types/search';
import { User, Search, TrendingUp, Book, Users, AlertCircle, RotateCcw, MessageCircle, Timer, Trophy, Compass } from 'lucide-react';
import { StreakDisplay } from '@/components/StreakDisplay';
import { AchievementNotificationBell } from '@/components/AchievementNotificationBell';
import { NotificationCenter } from '@/components/NotificationCenter';
import { SidebarTrigger } from '@/components/ui/sidebar';

export function Header() {
  const { user, session, signOut } = useAuth();
  const onboardingStatus = useOnboardingStatus();
  const { getTotalUnreadCount } = useChats();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [userProfile, setUserProfile] = useState<{ username?: string; name?: string; image?: string; archetype?: string } | null>(null);
  
  const isOnboardingOrAuth = location.pathname.startsWith('/onboarding') || location.pathname === '/auth';
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [bookResults, setBookResults] = useState<BookResult[]>([]);
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState<'title' | 'author'>('title');
  const [activeSearchTab, setActiveSearchTab] = useState('books');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [lastQuery, setLastQuery] = useState('');
  
  // Suggestions state
  const [suggestions, setSuggestions] = useState<BookResult[]>([]);
  const [suggestionsType, setSuggestionsType] = useState<'personalized' | 'popular'>('popular');
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  
  // Debug mode
  const isDebug = new URLSearchParams(window.location.search).has('debug');

  // Load user profile data for navigation
  useEffect(() => {
    if (user) {
      const loadUserProfile = async () => {
        const { data } = await supabase
          .from('profiles')
          .select('username, name, image, archetype')
          .eq('id', user.id)
          .maybeSingle();
        
        setUserProfile(data);
      };
      
      loadUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [user]);

  // Load suggestions when popover opens
  const loadSuggestions = useCallback(async () => {
    if (suggestions.length > 0) return; // Already loaded
    
    setSuggestionsLoading(true);
    try {
      console.log('[Header] Loading search suggestions...');
      const { data, error } = await supabase.functions.invoke('search-suggestions');
      
      if (error) throw error;
      
      console.log('[Header] Suggestions loaded:', data);
      setSuggestions(data.suggestions || []);
      setSuggestionsType(data.type || 'popular');
    } catch (error) {
      console.error('[Header] Error loading suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [suggestions.length]);

  // Load suggestions when search opens on Books tab
  useEffect(() => {
    if (searchOpen && activeSearchTab === 'books' && !searchQuery.trim()) {
      loadSuggestions();
    }
  }, [searchOpen, activeSearchTab, searchQuery, loadSuggestions]);

  // Search functionality
  const searchBooks = useCallback(async (searchQuery: string, type: 'title' | 'author' = 'title') => {
    const startTime = Date.now();
    setLastQuery(searchQuery);
    setSearchError(null);
    
    if (!searchQuery.trim()) {
      setBookResults([]);
      return;
    }

    setSearchLoading(true);
    let edgeCount = 0;
    let fallbackCount = 0;
    
    try {
      const { data: searchResults, error } = await supabase.functions.invoke('search-books', {
        body: { 
          query: searchQuery, 
          type, 
          page: 1,
          limit: 5
        }
      });

      if (error) throw error;

      if (searchResults?.results?.length > 0) {
        edgeCount = searchResults.results.length;
        setBookResults(searchResults.results);
        if (isDebug) console.log(`[DEBUG] Edge: ${edgeCount} results in ${Date.now() - startTime}ms`);
        return;
      }

      // If edge function returns 0 results on page 1, try Open Library fallback
      if (searchResults && searchResults.results?.length === 0) {
        console.log('Edge function returned 0 results, trying Open Library fallback...');
        const { results } = await searchOpenLibrary(searchQuery, type, 1, 5);
        fallbackCount = results.length;
        setBookResults(results);
        if (isDebug) console.log(`[DEBUG] Fallback: ${fallbackCount} results in ${Date.now() - startTime}ms`);
        return;
      }

      setBookResults([]);
    } catch (err) {
      console.error('Search error:', err);
      try {
        const { results } = await searchOpenLibrary(searchQuery, type, 1, 5);
        fallbackCount = results.length;
        setBookResults(results);
        if (isDebug) console.log(`[DEBUG] Error fallback: ${fallbackCount} results in ${Date.now() - startTime}ms`);
      } catch (fallbackErr) {
        console.error('Open Library fallback failed:', fallbackErr);
        setSearchError('Search failed. Please try again.');
        setBookResults([]);
      }
    } finally {
      setSearchLoading(false);
    }
  }, [isDebug]);

  const searchUsers = useCallback(async (searchQuery: string) => {
    console.log('searchUsers called with:', { searchQuery });
    
    if (!searchQuery.trim()) {
      console.log('Empty query, clearing user results');
      setUserResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      console.log('Searching users in database...');
      const { data: users } = await supabase
        .rpc('search_public_profiles', { 
          search_term: searchQuery, 
          excluding_user_id: user?.id 
        })
        .limit(5);

      console.log('User search results:', users);

      if (users) {
        const { data: follows } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', user?.id)
          .in('following_id', users.map(u => u.id));

        const followingIds = new Set(follows?.map(f => f.following_id) || []);
        const usersWithFollowStatus = users.map(user => ({
          ...user,
          isFollowing: followingIds.has(user.id),
        }));

        console.log('Setting user results:', usersWithFollowStatus.length, 'users');
        setUserResults(usersWithFollowStatus);
      } else {
        console.log('No users found, clearing results');
        setUserResults([]);
      }
    } catch (error) {
      console.error('User search error:', error);
      setUserResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [user?.id]);

  // Clear results when switching tabs
  useEffect(() => {
    if (activeSearchTab === 'books') {
      setUserResults([]);
    } else {
      setBookResults([]);
    }
  }, [activeSearchTab]);

  // Debounced search
  useEffect(() => {
    console.log('Search effect triggered:', { searchQuery, activeSearchTab, searchType });
    
    if (!searchQuery.trim()) {
      setBookResults([]);
      setUserResults([]);
      return;
    }

    const timer = setTimeout(() => {
      console.log('Executing search for:', searchQuery, 'tab:', activeSearchTab);
      if (activeSearchTab === 'books') {
        searchBooks(searchQuery, searchType);
      } else {
        searchUsers(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchType, activeSearchTab, searchBooks, searchUsers]);

  const handleBookClick = async (book: BookResult) => {
    setSearchOpen(false);
    setSearchQuery('');
    
    try {
      // If it's a local book, navigate directly
      if (book.isLocal && book.id) {
        navigate(`/book/${book.id}`);
        return;
      }

      const normalizedId = normalizeOpenLibraryId(book.open_library_id || '');
      const hasISBN = book.isbn13 || book.isbn10;
      
      // Must have either OLID or ISBN
      if (!normalizedId && !hasISBN) {
        throw new Error('Invalid book identifier');
      }
      
      // Build dynamic query based on available identifiers
      const queryParts: string[] = [];
      if (normalizedId) {
        queryParts.push(`open_library_id.eq.${normalizedId}`);
        queryParts.push(`open_library_id.eq./works/${normalizedId}`);
      }
      if (book.isbn13) {
        queryParts.push(`isbn13.eq.${book.isbn13}`);
        queryParts.push(`canonical_isbn13.eq.${book.isbn13}`);
      }
      if (book.isbn10) {
        queryParts.push(`isbn10.eq.${book.isbn10}`);
      }
      
      // Check for existing book with multi-factor validation
      const { data: existingBooks } = await supabase
        .from('books')
        .select('id, published_year, isbn13, isbn10, open_library_id, canonical_isbn13')
        .or(queryParts.join(','))
        .limit(5);

      // Validate it's actually the same book using multiple factors
      const matchingBook = existingBooks?.find(existing => {
        const yearMatch = !book.published_year || !existing.published_year || 
                          existing.published_year === book.published_year;
        const isbnMatch = (book.isbn13 && book.isbn13 === existing.isbn13) || 
                         (book.isbn10 && book.isbn10 === existing.isbn10) ||
                         (book.isbn13 && book.isbn13 === existing.canonical_isbn13);
        const olidMatch = normalizedId && (existing.open_library_id === normalizedId || 
                         existing.open_library_id === `/works/${normalizedId}`);
        
        // Must match on OLID + year, OR have matching ISBN
        return (olidMatch && yearMatch) || isbnMatch;
      });

      if (matchingBook) {
        navigate(`/book/${matchingBook.id}`);
        return;
      }

      // Create new book - OLID is optional, ISBN can be primary identifier
      const bookData = {
        ...(normalizedId && { open_library_id: normalizedId }),
        title: book.title || 'Untitled',
        subtitle: book.subtitle,
        authors: book.authors || [],
        published_year: book.published_year,
        cover_url: book.cover_url,
        isbn10: book.isbn10,
        isbn13: book.isbn13,
        synopsis: book.synopsis,
        source: normalizedId ? 'openlibrary' : 'isbndb',
        language: 'en',
        enrichment_status: 'pending'
      };

      const { data: newBook, error } = await supabase
        .from('books')
        .insert(bookData)
        .select('id')
        .single();

      if (error) {
        // Handle duplicate ISBN constraint violation
        if (error.code === '23505' && book.isbn13) {
          const { data: existingByIsbn } = await supabase
            .from('books')
            .select('id')
            .or(`isbn13.eq.${book.isbn13},canonical_isbn13.eq.${book.isbn13}`)
            .limit(1)
            .single();
          
          if (existingByIsbn?.id) {
            navigate(`/book/${existingByIsbn.id}`);
            return;
          }
        }
        throw error;
      }
      if (!newBook?.id) throw new Error('Failed to create book');

      navigate(`/book/${newBook.id}`);
    } catch (error) {
      console.error('Error handling book click:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "There was an error opening the book details.",
      });
    }
  };

  const retrySearch = () => {
    if (lastQuery) {
      searchBooks(lastQuery, searchType);
    }
  };

  const handleViewPublicProfile = () => {
    if (userProfile?.username) {
      navigate(`/u/${userProfile.username}`);
    } else {
      navigate('/onboarding');
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-secondary-light/30 bg-[hsl(var(--secondary))] shadow-soft text-secondary-foreground">
      <div className="container flex h-[var(--header-height)] items-center">
        {/* Sidebar Trigger - show on all screens except onboarding/auth */}
        {!isOnboardingOrAuth && <SidebarTrigger className="mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center" />}
        
        {/* Logo as Home Button */}
        <Link to="/" className="flex items-center">
          <img src={dinoLogoFull} alt="Dino - Home" className="h-16 w-auto hover:opacity-80 transition-opacity" />
        </Link>

        <div className="flex flex-1 items-center justify-end space-x-2 md:space-x-4">
          {/* Streak Display */}
          {user && session && <StreakDisplay />}

          {/* Notification Center - combined on mobile */}
          {user && session && <NotificationCenter />}

          {/* Achievement Notifications - hidden on mobile, shown in More sheet */}
          {user && session && <div className="hidden md:block"><AchievementNotificationBell /></div>}

          {/* Messages Icon - hidden on mobile, in bottom nav */}
          {user && session && (
            <Button
              variant="ghost"
              size="sm"
              className="relative hidden md:flex"
              onClick={() => navigate('/inbox')}
            >
              <MessageCircle className="h-5 w-5" />
              {getTotalUnreadCount() > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 min-w-5 text-xs px-1 flex items-center justify-center"
                >
                  {getTotalUnreadCount() >= 9 ? '9+' : getTotalUnreadCount()}
                </Badge>
              )}
            </Button>
          )}

          {/* Search Bar - hidden on mobile, users can access via Discover page */}
          {user && session && (
            <Popover open={searchOpen} onOpenChange={setSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => setSearchOpen(true)}
                  className="relative h-10 w-48 lg:w-64 justify-start text-sm font-normal text-muted-foreground hidden md:flex"
                >
                  <Search className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="truncate">Search books...</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-96 p-0 bg-background z-50" 
                align="end"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="p-4 space-y-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      navigate('/search');
                      setSearchOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    Go to full search page →
                  </Button>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      autoFocus
                    />
                  </div>

                  <Tabs value={activeSearchTab} onValueChange={setActiveSearchTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="books" className="flex items-center gap-2">
                        <Book className="w-4 h-4" />
                        Books
                      </TabsTrigger>
                      <TabsTrigger value="people" className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        People
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="books" className="space-y-2">
                      {activeSearchTab === 'books' && (
                        <div className="flex justify-center mb-2">
                          <div className="inline-flex rounded-lg border p-1">
                            <button
                              onClick={() => setSearchType('title')}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                searchType === 'title'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              Title
                            </button>
                            <button
                              onClick={() => setSearchType('author')}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                searchType === 'author'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              Author
                            </button>
                          </div>
                        </div>
                      )}

                      {searchError ? (
                        <div className="text-center py-4 space-y-3">
                          <AlertCircle className="w-8 h-8 text-destructive mx-auto" />
                          <div className="text-sm text-muted-foreground">{searchError}</div>
                          <Button variant="outline" size="sm" onClick={retrySearch}>
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Try again
                          </Button>
                        </div>
                      ) : searchLoading ? (
                        <div className="space-y-2">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
                              <div className="w-10 h-14 bg-muted rounded" />
                              <div className="flex-1 space-y-1">
                                <div className="h-3 bg-muted rounded w-3/4" />
                                <div className="h-2 bg-muted/60 rounded w-1/2" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : bookResults.length > 0 ? (
                        <div className="max-h-64 overflow-y-auto space-y-1">
                          {bookResults.map((book, index) => (
                            <Button
                              key={book.id || book.open_library_id || index}
                              variant="ghost"
                              className="w-full justify-start p-2 h-auto"
                              onClick={() => handleBookClick(book)}
                            >
                              <div className="flex items-center gap-3 w-full">
                                <CoverImage
                                  bookId={book.id}
                                  isbn13={book.isbn13}
                                  isbn10={book.isbn10}
                                  olid={book.open_library_id}
                                  coverUrl={book.cover_url}
                                  title={book.title}
                                  author={book.authors?.[0]}
                                  size="xs"
                                  className="w-8 h-12 flex-shrink-0"
                                />
                                <div className="flex-1 text-left">
                                  <div className="font-medium text-sm truncate">{book.title || 'Untitled'}</div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {book.authors?.length ? book.authors.join(', ') : 'Unknown author'}
                                  </div>
                                </div>
                                {!book.isLocal && (
                                  <Badge variant="secondary" className="text-xs">
                                    New
                                  </Badge>
                                )}
                              </div>
                            </Button>
                          ))}
                          {isDebug && (
                            <div className="text-xs text-muted-foreground px-2 py-1">
                              {bookResults.length} results loaded
                            </div>
                          )}
                        </div>
                      ) : !searchQuery.trim() && suggestions.length > 0 ? (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                            {suggestionsType === 'personalized' ? 'Books by authors you\'ve read' : 'Popular books'}
                          </div>
                          <div className="max-h-64 overflow-y-auto space-y-1">
                            {suggestions.map((book, index) => (
                              <Button
                                key={book.id || index}
                                variant="ghost"
                                className="w-full justify-start p-2 h-auto"
                                onClick={() => handleBookClick(book)}
                              >
                                <div className="flex items-center gap-3 w-full">
                                  <CoverImage
                                    bookId={book.id}
                                    isbn13={book.isbn13}
                                    isbn10={book.isbn10}
                                    olid={book.open_library_id}
                                    coverUrl={book.cover_url}
                                    title={book.title}
                                    author={book.authors?.[0]}
                                    size="xs"
                                    className="w-8 h-12 flex-shrink-0"
                                  />
                                  <div className="flex-1 text-left">
                                    <div className="font-medium text-sm truncate">{book.title || 'Untitled'}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                      {book.authors?.length ? book.authors.join(', ') : 'Unknown author'}
                                    </div>
                                  </div>
                                </div>
                              </Button>
                            ))}
                          </div>
                        </div>
                      ) : !searchQuery.trim() && suggestionsLoading ? (
                        <div className="space-y-2">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
                              <div className="w-10 h-14 bg-muted rounded" />
                              <div className="flex-1 space-y-1">
                                <div className="h-3 bg-muted rounded w-3/4" />
                                <div className="h-2 bg-muted/60 rounded w-1/2" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : searchQuery && !searchLoading ? (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          No books found for "{searchQuery}"
                        </div>
                      ) : null}
                      
                      {searchQuery && bookResults.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => {
                            navigate(`/search?q=${encodeURIComponent(searchQuery)}&tab=books`);
                            setSearchOpen(false);
                            setSearchQuery('');
                          }}
                        >
                          View all book results
                        </Button>
                      )}
                    </TabsContent>

                    <TabsContent value="people" className="space-y-2">
                      {searchLoading ? (
                        <div className="space-y-2">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
                              <div className="w-8 h-8 bg-muted rounded-full" />
                              <div className="flex-1 space-y-1">
                                <div className="h-3 bg-muted rounded w-1/2" />
                                <div className="h-2 bg-muted/60 rounded w-1/3" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : userResults.length > 0 ? (
                        <div className="max-h-64 overflow-y-auto space-y-1">
                          {userResults.map((person) => (
                            <Button
                              key={person.id}
                              variant="ghost"
                              className="w-full justify-start p-2 h-auto"
                              onClick={() => {
                                navigate(`/u/${person.username}`);
                                setSearchOpen(false);
                                setSearchQuery('');
                              }}
                            >
                              <div className="flex items-center gap-3 w-full">
                                <DinoAvatar 
                                  src={person.image}
                                  name={person.name || person.username}
                                  size="xs"
                                />
                                <div className="flex-1 text-left">
                                  <div className="font-medium text-sm truncate">
                                    {person.name || person.username}
                                  </div>
                                  {person.username && person.name && (
                                    <div className="text-xs text-muted-foreground truncate">
                                      @{person.username}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Button>
                          ))}
                        </div>
                      ) : searchQuery && !searchLoading ? (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          No people found for "{searchQuery}"
                        </div>
                      ) : null}

                      {searchQuery && userResults.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => {
                            navigate(`/search?q=${encodeURIComponent(searchQuery)}&tab=people`);
                            setSearchOpen(false);
                            setSearchQuery('');
                          }}
                        >
                          View all people results
                        </Button>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Safe Mode Banner (Dev only) */}
          {import.meta.env.DEV && import.meta.env.VITE_SAFE_MODE === 'true' && (
            <div className="px-2 py-1 bg-yellow-500 text-yellow-900 text-xs rounded font-medium">
              SAFE MODE
            </div>
          )}

          {user && session ? (
            <Button 
              variant="ghost" 
              className="relative h-10 w-10 rounded-full"
              onClick={handleViewPublicProfile}
            >
              <DinoAvatar
                src={userProfile?.image || user.user_metadata?.avatar_url || user.user_metadata?.image}
                name={userProfile?.name || user.user_metadata?.full_name || user.user_metadata?.name || user.email}
                size="sm"
              />
            </Button>
          ) : (
            <div className="flex items-center space-x-3">
              <Button variant="pill" asChild>
                <Link to="/auth?mode=signin">Sign In</Link>
              </Button>
              <Button variant="pillOutline" asChild>
                <Link to="/auth?mode=signup">Sign Up</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}